import { i18n } from '../i18n.js';
import { MoonfallConsent } from '../analytics/consent-state.js';
import { LEGAL_VERSIONS } from '../legal-versions.js';
import { MoonfallCloud } from '../convex-client.js';
import { RegionAvailability } from '../region-availability.js';
import { BootVeil } from './boot-veil.js';
import { AuthWaitBar } from './auth-wait-bar.js';
/** Consent gate for registration and legal-version updates. */


let ConsentGate = {};

ConsentGate = (function () {
  let overlay = null;
  let resolvePromise = null;
  let overlayConfig = null;

  // Was `LEGAL_VERSIONS || { ...a second copy of the versions... }`.
  // The import cannot be missing, so the fallback is gone — and it was a
  // liability rather than safety: a stale duplicate here would have quietly
  // reported an older legal version and skipped the re-consent prompt the
  // update is for. (origin/main's copy had already drifted to
  // privacy 2026-08-07-v2 while this file's said something else.)
  const CURRENT_TOS_VERSION = LEGAL_VERSIONS.tos;
  const CURRENT_PRIVACY_VERSION = LEGAL_VERSIONS.privacy;
  const CURRENT_MARKETING_VERSION = LEGAL_VERSIONS.marketing;
  const CURRENT_EMAIL_MARKETING_VERSION = LEGAL_VERSIONS.emailMarketing;
  const MINIMUM_AGE = 16;

  /* How each OPTIONAL box starts. The server decides this from the visitor's
     country (game/utils/consentDefaults.js): a box starts ticked only where the
     law allows it. With no answer, every box starts empty. The terms box is
     not governed: it carries the age declaration and always starts empty. */
  function normalizeDefaults(defaults) {
    const source = defaults && typeof defaults === "object" ? defaults : {};
    const pick = (key) => (source[key] === "preticked" ? "preticked" : "unticked");
    return { analytics: pick("analytics"), email: pick("email") };
  }

  function applyReplacements(str, replacements) {
    return String(str).replace(/\{\{(\w+)\}\}/g, (match, token) =>
      Object.prototype.hasOwnProperty.call(replacements, token) ? String(replacements[token]) : match
    );
  }

  function uiText(key, fallback, replacements = {}) {
    const translated = i18n?.t?.(key, replacements);
    // The fallback must also honor {{tokens}} — otherwise, before i18n loads or
    // when a key is missing, users see a literal "{{age}}" in the consent gate.
    return translated && translated !== key ? translated : applyReplacements(fallback, replacements);
  }

  function el(tag, attrs, children) {
    const node = document.createElement(tag);
    if (attrs) {
      Object.entries(attrs).forEach(([k, v]) => {
        if (k === "className") node.className = v;
        else if (k === "textContent") node.textContent = v;
        else if (k === "disabled") node.disabled = v;
        else if (k === "htmlFor") node.htmlFor = v;
        else node.setAttribute(k, v);
      });
    }
    (children || []).forEach((child) => {
      if (typeof child === "string") {
        node.appendChild(document.createTextNode(child));
      } else if (child) {
        node.appendChild(child);
      }
    });
    return node;
  }

  function createCheckRow(inputId, labelChildren, options) {
    const row = el("div", { className: "consent-gate-check" });
    const checkbox = el("input", { type: "checkbox", id: inputId });
    if (options && options.checked) {
      checkbox.checked = true;
      checkbox.setAttribute("checked", "");
    }
    const label = el("label", { htmlFor: inputId });
    labelChildren.forEach((child) => {
      if (typeof child === "string") {
        label.appendChild(document.createTextNode(child));
      } else {
        label.appendChild(child);
      }
    });
    row.appendChild(checkbox);
    row.appendChild(label);
    return row;
  }

  function createInfoRow(children) {
    return el("div", { className: "consent-gate-info" }, [
      el("p", null, children),
    ]);
  }

  function createUpdatedBadge() {
    return el("span", { className: "consent-gate-updated-badge", textContent: uiText("consentGate.badgeUpdated", "updated") });
  }

  function normalizeConfig(modeOrConfig) {
    if (!modeOrConfig || typeof modeOrConfig === "string") {
      const isReconsent = modeOrConfig === "reconsent";
      return {
        mode: isReconsent ? "reconsent" : "new",
        requireTermsCheckbox: true,
        recordTos: true,
        recordPrivacy: true,
        highlightTos: isReconsent,
        highlightPrivacy: isReconsent,
        defaults: normalizeDefaults(null),
      };
    }

    return {
      mode: modeOrConfig.mode === "reconsent" ? "reconsent" : "new",
      requireTermsCheckbox: modeOrConfig.requireTermsCheckbox !== false,
      recordTos: modeOrConfig.recordTos !== false,
      recordPrivacy: Boolean(modeOrConfig.recordPrivacy),
      highlightTos: Boolean(modeOrConfig.highlightTos),
      highlightPrivacy: Boolean(modeOrConfig.highlightPrivacy),
      offerMarketing: Boolean(modeOrConfig.offerMarketing),
      offerEmail: Boolean(modeOrConfig.offerEmail),
      defaults: normalizeDefaults(modeOrConfig.defaults),
      /* Carried through the normalizer too, or a caller that passes a config
         built by `buildGateConfig` would silently drop back to the bundle's
         constants -- which is the whole loop. */
      tosVersion: modeOrConfig.tosVersion || CURRENT_TOS_VERSION,
      privacyVersion: modeOrConfig.privacyVersion || CURRENT_PRIVACY_VERSION,
      marketingVersion: modeOrConfig.marketingVersion || CURRENT_MARKETING_VERSION,
      emailVersion: modeOrConfig.emailVersion || CURRENT_EMAIL_MARKETING_VERSION,
    };
  }

  function buildGateConfig(status, defaults) {
    const isNewUser = Boolean(status?.isNewUser);
    const tosOutdated = Boolean(status?.tosOutdated);
    const privacyOutdated = Boolean(status?.privacyOutdated);

    return {
      mode: isNewUser ? "new" : "reconsent",
      requireTermsCheckbox: isNewUser || tosOutdated,
      recordTos: isNewUser || tosOutdated,
      recordPrivacy: isNewUser || privacyOutdated,
      highlightTos: !isNewUser && tosOutdated,
      highlightPrivacy: !isNewUser && privacyOutdated,
      /*
       * THE VERSION COMES FROM THE SERVER, WITH THE BUNDLE AS A FALLBACK.
       *
       * `marketingVersion` already did this; tos and privacy read their
       * compiled constants instead, and that difference is a loop. A bundle
       * cached across a legal-version bump records the version IT was built
       * with, the server compares against ITS own and still says outdated, and
       * the gate returns forever. Asking the server what it serves ends it
       * without a reload.
       *
       * It is truthful, because the gate does not carry the legal TEXT: it
       * links to `/terms` and `/privacy`, which this same server renders at its
       * current version. The text the player reads is the version recorded.
       * The server still refuses anything that does not match
       * (`assertRecordableConsentVersion`), so this is a convenience for the
       * honest client, never the authority.
       */
      tosVersion: status?.currentTosVersion || CURRENT_TOS_VERSION,
      privacyVersion: status?.currentPrivacyVersion || CURRENT_PRIVACY_VERSION,
      // Optional opt-ins: only ever surfaced, never required.
      offerMarketing: Boolean(status?.marketingOffered),
      marketingVersion: status?.currentMarketingVersion || CURRENT_MARKETING_VERSION,
      offerEmail: Boolean(status?.emailMarketingOffered),
      emailVersion: status?.currentEmailMarketingVersion || CURRENT_EMAIL_MARKETING_VERSION,
      defaults: normalizeDefaults(defaults),
    };
  }

  function getTitle(config) {
    if (config.mode !== "reconsent") return uiText("consentGate.titleWelcome", "Welcome to Moonfall");
    if (config.highlightTos) return uiText("consentGate.titleTos", "We've Updated Our Terms");
    if (config.highlightPrivacy) return uiText("consentGate.titlePrivacy", "We've Updated Our Privacy Policy");
    return uiText("consentGate.titleLegal", "Legal Update");
  }

  function getSubtitle(config) {
    if (config.mode !== "reconsent") return uiText("consentGate.subtitleNew", "Review the Terms of Service and Privacy Policy before creating your account.");
    if (config.requireTermsCheckbox) return uiText("consentGate.subtitleAccept", "Please review and accept the updated legal terms to continue.");
    return uiText("consentGate.subtitleReview", "Please review the updated Privacy Policy to continue.");
  }

  function getSubmitText(config) {
    if (config.mode !== "reconsent") return uiText("consentGate.submitCreate", "Create Account");
    return config.requireTermsCheckbox ? uiText("consentGate.submitAccept", "Accept & Continue") : uiText("consentGate.submitContinue", "Continue");
  }

  function createOverlay(config) {
    const isReconsent = config.mode === "reconsent";

    const container = el("div", { className: "consent-gate-overlay" });
    const modal = el("div", { className: "consent-gate-modal" + (isReconsent ? " re-consent" : "") });

    modal.appendChild(el("h2", {
      className: "consent-gate-title",
      textContent: getTitle(config),
    }));

    modal.appendChild(el("p", {
      className: "consent-gate-subtitle",
      textContent: getSubtitle(config),
    }));

    const content = el("div", { className: "consent-gate-checkboxes" });

    if (config.requireTermsCheckbox) {
      const tosLink = el("a", { href: "/terms", target: "_blank", rel: "noopener" }, [uiText("consentGate.tosLinkText", "Terms of Service")]);
      const tosLabelParts = isReconsent
        ? [uiText("consentGate.tosAgreePrefix", "I agree to the "), tosLink]
        : [
          uiText("consentGate.tosAgreePrefix", "I agree to the "),
          tosLink,
          uiText(
            "consentGate.tosAgeSuffix",
            " and confirm that I am at least {{age}} years old. If I am under the age of majority in my jurisdiction, I confirm that my parent or legal guardian has reviewed and agrees to these Terms on my behalf.",
            { age: MINIMUM_AGE },
          ),
        ];
      if (config.highlightTos) {
        tosLabelParts.push(createUpdatedBadge());
      }
      // Never pre-ticked, in any country: this box is also the age declaration.
      content.appendChild(createCheckRow("consent-tos", tosLabelParts));
    }

    const privacyLink = el("a", { href: "/privacy", target: "_blank", rel: "noopener" }, [uiText("consentGate.privacyLinkText", "Privacy Policy")]);
    const privacyNoticeParts = [
      isReconsent
        ? uiText("consentGate.privacyReviewPrefix", "Please review the ")
        : uiText("consentGate.privacyDataPrefix", "Your data is handled as described in the "),
      privacyLink,
      uiText("consentGate.privacySuffix", "."),
    ];
    if (config.highlightPrivacy) {
      privacyNoticeParts.splice(2, 0, createUpdatedBadge());
    }
    content.appendChild(createInfoRow(privacyNoticeParts));

    // Optional choices, one block, two separate boxes: measurement and email
    // are different purposes, so each is its own consent. Neither ever blocks
    // the mandatory submit (GDPR "freely given" — declining is fine). Whether a
    // box starts ticked is the server's call per country (`config.defaults`).
    if (config.offerMarketing) {
      const marketingRow = createCheckRow("consent-marketing", [
        el("strong", { className: "consent-gate-optional-title", textContent: uiText("consentGate.analyticsTitle", "Analytics") }),
        el("span", { className: "consent-gate-optional-tag", textContent: uiText("consentGate.marketingTag", "Optional") }),
        el("span", {
          className: "consent-gate-optional-desc",
          textContent: uiText("consentGate.analyticsDesc", "Let us measure how Moonfall is used and how players find it, so we can improve the game."),
        }),
      ], { checked: config.defaults.analytics === "preticked" });
      marketingRow.classList.add("consent-gate-optional");
      content.appendChild(marketingRow);
    }
    if (config.offerEmail) {
      const emailRow = createCheckRow("consent-email", [
        el("strong", { className: "consent-gate-optional-title", textContent: uiText("consentGate.emailTitle", "Email news") }),
        el("span", { className: "consent-gate-optional-tag", textContent: uiText("consentGate.marketingTag", "Optional") }),
        el("span", {
          className: "consent-gate-optional-desc",
          textContent: uiText("consentGate.emailDesc", "Send me news about Moonfall — new sets, events and offers. Unsubscribe anytime."),
        }),
      ], { checked: config.defaults.email === "preticked" });
      emailRow.classList.add("consent-gate-optional");
      content.appendChild(emailRow);
    }

    modal.appendChild(content);

    const submitBtn = el("button", {
      className: "consent-gate-submit",
      id: "consent-submit",
      disabled: config.requireTermsCheckbox,
      textContent: getSubmitText(config),
    });
    modal.appendChild(submitBtn);

    container.appendChild(modal);
    return container;
  }

  function removeOverlayNode(node) {
    if (node && node.parentNode) {
      node.parentNode.removeChild(node);
    }
  }

  function updateButtonState() {
    if (!overlay) return;
    const tosCheck = overlay.querySelector("#consent-tos");
    const btn = overlay.querySelector("#consent-submit");
    if (!btn) return;
    btn.disabled = Boolean(tosCheck) && !tosCheck.checked;
  }

  function handleSubmit() {
    const resolver = resolvePromise;
    const consents = {};
    if (overlayConfig?.recordTos) {
      consents.tos = overlayConfig.tosVersion || CURRENT_TOS_VERSION;
    }
    if (overlayConfig?.recordPrivacy) {
      consents.privacy = overlayConfig.privacyVersion || CURRENT_PRIVACY_VERSION;
    }
    if (overlayConfig?.offerMarketing) {
      const marketingCheck = overlay && overlay.querySelector("#consent-marketing");
      consents.marketing = {
        version: overlayConfig.marketingVersion || CURRENT_MARKETING_VERSION,
        granted: Boolean(marketingCheck && marketingCheck.checked),
      };
    }
    if (overlayConfig?.offerEmail) {
      const emailCheck = overlay && overlay.querySelector("#consent-email");
      consents.emailMarketing = {
        version: overlayConfig.emailVersion || CURRENT_EMAIL_MARKETING_VERSION,
        granted: Boolean(emailCheck && emailCheck.checked),
      };
    }

    resolvePromise = null;
    /* THE GATE STAYS UP, BUSY, UNTIL THE NEXT SURFACE REPLACES IT.
       It used to close here, and what was under it was the SIGN-IN CARD — so a
       new player who had just signed in and accepted the terms watched
       "Continue with Google" come back for the ~7 s the account took to create
       (measured by the snappiness meter), as if the sign-in had failed. Now the
       button shows the shared pending state and main.js releases the gate the
       moment the username card is on screen, or the finished lobby is; a cap
       releases it regardless. */
    markSubmitting();
    if (resolver) {
      resolver(consents);
    }
  }

  const HANDOVER_CAP_MS = 20000;
  /* How often a held gate under a later step looks again for that step to be done. */
  const LATER_STEP_RECHECK_MS = 1000;
  /* The longest one consent write may take, its answer included (recordConsents). */
  const CONSENT_WRITE_TIMEOUT_MS = 20000;
  let handoverTimer = null;

  /* A LATER STEP OF THE SAME SIGN-IN MAY SIT OVER THE HELD GATE, in the
     gate's own frame: the email check a new unverified account meets
     (ui/email-verification-gate.js) and Cloudflare's challenge once it asks
     the player to interact (turnstile-challenge.js). Both are
     `.consent-gate-overlay` siblings appended after the gate, so they paint
     above it at the same z-index. The step is the player's, for as long as
     they take, with its own ways out; leaving at the cap under it would put
     the sign-in card behind the step's translucent backdrop, and in front of
     the player once the step is done while the account is still being
     created. So the cap waits for the step to be done, then starts again. */
  function laterStepOver(gate) {
    return Array.from(document.querySelectorAll('.consent-gate-overlay')).some((layer) => layer !== gate
      && (layer.classList.contains('visible') || layer.classList.contains('interactive')));
  }

  function armHandoverCap() {
    clearTimeout(handoverTimer);
    handoverTimer = setTimeout(leaveAtCap, HANDOVER_CAP_MS);
  }

  function leaveAtCap() {
    const gate = overlay;
    if (!gate || !laterStepOver(gate)) {
      hide({ preservePromise: true });
      return;
    }
    handoverTimer = setTimeout(function stepDone() {
      if (overlay !== gate) return;
      if (laterStepOver(gate)) {
        handoverTimer = setTimeout(stepDone, LATER_STEP_RECHECK_MS);
        return;
      }
      armHandoverCap();
    }, LATER_STEP_RECHECK_MS);
  }

  function markSubmitting() {
    if (!overlay) return;
    /* Opaque from the press until the release: a returning player's lobby is
       built UNDER the gate, and at 92 % black its pieces appeared one by one
       through the blur (the username card's measured twin defect). */
    overlay.classList.add('is-submitting');
    overlay.querySelectorAll('input, button').forEach((el) => { el.disabled = true; });
    const submit = overlay.querySelector('#consent-submit');
    if (submit) {
      submit.setAttribute('aria-busy', 'true');
      submit.classList.add('btn-pending');
      /* The account is created now (1.9-5.8 s on round 15): past the grace
         the button also carries the one bar (ui/auth-wait-bar.js). */
      AuthWaitBar.arm(submit);
    }
    armHandoverCap();
  }

  /** The next surface is on screen, or the login was refused: take the gate
      away (no-op when it is gone). */
  function release() {
    hide({ preservePromise: true });
  }

  function show(modeOrConfig) {
    if (overlay) hide({ immediate: true });

    overlayConfig = normalizeConfig(modeOrConfig);
    overlay = createOverlay(overlayConfig);
    document.body.appendChild(overlay);
    // At boot the gate is the first surface: the loading veil comes down on it.
    BootVeil.drop('consent-gate');

    // Attach listeners
    overlay.querySelectorAll('input[type="checkbox"]').forEach((cb) => {
      cb.addEventListener("change", updateButtonState);
    });
    overlay.querySelector("#consent-submit").addEventListener("click", handleSubmit);

    // Trigger visibility with animation frame
    const shown = overlay;
    requestAnimationFrame(() => {
      shown.classList.add("visible");
      /* The sign-in's wait ends in the frame the gate appears: its bar does
         not run on under the gate's blur. */
      if (overlay === shown) AuthWaitBar.disarmAll();
    });

    return new Promise((resolve) => {
      resolvePromise = resolve;
    });
  }

  function hide(options) {
    const currentOverlay = overlay;
    const { immediate = false, preservePromise = false } = options || {};
    if (!currentOverlay) return;

    /* The cap belongs to the gate it was armed for: left running, an earlier
       Accept's cap took a newer gate down with its promise still pending. */
    clearTimeout(handoverTimer);
    handoverTimer = null;
    overlay = null;
    overlayConfig = null;
    AuthWaitBar.disarm(currentOverlay.querySelector("#consent-submit"));
    currentOverlay.classList.remove("visible");

    if (!preservePromise && resolvePromise) {
      resolvePromise(null);
      resolvePromise = null;
    }

    if (immediate) {
      removeOverlayNode(currentOverlay);
      return;
    }

    setTimeout(() => {
      removeOverlayNode(currentOverlay);
    }, 300);
  }

  /**
   * Tag a failure with the step that produced it.
   *
   * Only ONE of the ways this module fails is about the player's legal consent.
   * The rest are a request to the deployment that never completed — an
   * uninitialized Convex client, an offline browser, a deployment that is down.
   * The caller cannot tell them apart from a message, so it used to alert "We
   * could not verify your legal consent" for all of them and send the player
   * looking for a legal problem that did not exist (reported 2026-08-13).
   * The step is known for certain here and nowhere else.
   */
  function consentFailure(code, message, cause) {
    // `data` carries a server-stated reason (ConvexError `data`) when there is
    // one — the only diagnosis anyone downstream may repeat. `cause` keeps the
    // original failure so tagging never costs a developer the real stack.
    return Object.assign(
      new Error(message, cause === undefined ? undefined : { cause }),
      { code, data: cause?.data },
    );
  }

  /**
   * Check consent and show gate if needed.
   * Call after Firebase auth and Convex auth setup.
   *
   * Fail-closed: throws on any error, tagged with `code`. Callers must NOT let
   * the user proceed into the app if this throws.
   *
   * Note that an OUTDATED legal version is not a failure: it resolves through
   * the gate below as a re-consent prompt. Nothing here turns a version bump
   * into an error the player cannot clear.
   *
   * Returns consent data if the gate was shown (user agreed),
   * or null if no consent was needed.
   */
  async function checkAndShow() {
    let status;
    try {
      status = await MoonfallCloud.query("gdpr:checkConsentVersion", {});
    } catch (requestError) {
      throw consentFailure("consent_request_failed", "Consent check request failed", requestError);
    }

    if (!status) {
      throw consentFailure("consent_response_invalid", "Consent status response was empty");
    }

    if (!status.needsConsent) return null;

    // Presentation only, and it never rejects: with no answer every box starts
    // empty. Asked after the status so a gate that is not needed costs nothing.
    const defaults = await RegionAvailability.getConsentDefaults();

    return await show(buildGateConfig(status, defaults));
  }

  /**
   * Record consent grants to Convex via server API.
   *
   * Fail-closed: throws on any non-2xx response or network error.
   * Callers must NOT let the user proceed if any consent write fails.
   */
  async function recordConsents(consents, authToken) {
    if (!consents) {
      throw new Error("recordConsents called without consent data");
    }
    if (!authToken) {
      throw new Error("recordConsents called without auth token");
    }

    const headers = {
      "Content-Type": "application/json",
      Authorization: "Bearer " + authToken,
    };

    /* Each write is dropped at CONSENT_WRITE_TIMEOUT_MS, its answer included:
       a mandatory one then refuses the login like any failed write, and an
       optional one is a failed decision. An unanswered write used to hold the
       player forever (the name card's "Enter Moonfall" waits for this). */
    const postConsent = async (consentType, version, extra) => {
      const controller = typeof AbortController === "function" ? new AbortController() : null;
      const timer = controller ? setTimeout(() => controller.abort(), CONSENT_WRITE_TIMEOUT_MS) : null;
      try {
        const res = await fetch("/api/gdpr/consent", {
          method: "POST",
          headers,
          body: JSON.stringify(Object.assign({ consentType, version }, extra || {})),
          ...(controller ? { signal: controller.signal } : {}),
        });
        if (!res.ok) {
          let detail = "";
          try {
            const body = await res.json();
            detail = body.error || "";
          } catch (_) {
            // ignore
          }
          throw new Error(
            "Consent recording failed (" + consentType + "): " + res.status + " " + detail
          );
        }
        return res;
      } catch (err) {
        if (controller && controller.signal.aborted) {
          throw new Error("Consent recording timed out (" + consentType + ")", { cause: err });
        }
        throw err;
      } finally {
        if (timer) clearTimeout(timer);
      }
    };

    // Mandatory consents are recorded first and must succeed.
    const tasks = [];
    if (consents.tos) tasks.push(postConsent("tos", consents.tos));
    if (consents.privacy) tasks.push(postConsent("privacy", consents.privacy));
    await Promise.all(tasks);

    // Optional decisions (grant or decline) are best-effort: a failure here
    // must never block access. Persisting attribution is handled by the caller
    // once consent is on record. `source: "gate"` tells the server which
    // presentation rule the box was shown under. The two are separate
    // decisions on separate records, so they are written together: one round
    // trip after the mandatory ones, not one each (round 15 of the
    // snappiness meter measured the three in a row on the new player's path).
    const optional = [];
    if (consents.emailMarketing && consents.emailMarketing.version) {
      optional.push(postConsent("email_marketing", consents.emailMarketing.version, {
        granted: consents.emailMarketing.granted === true,
        source: "gate",
      }).catch((err) => {
        if (window.CLIENT_DEBUG_MODE) console.warn("[ConsentGate] email consent failed:", err);
      }));
    }
    if (consents.marketing && consents.marketing.version) {
      optional.push(postConsent("marketing", consents.marketing.version, {
        granted: consents.marketing.granted === true,
        source: "gate",
      }).then(() => {
        if (MoonfallConsent) {
          MoonfallConsent.setAnalyticsConsent(consents.marketing.granted === true);
        }
      }, (err) => {
        if (MoonfallConsent) {
          MoonfallConsent.setAnalyticsConsent(false);
        }
        if (window.CLIENT_DEBUG_MODE) console.warn("[ConsentGate] marketing consent failed:", err);
      }));
    }
    await Promise.all(optional);
  }

  return {
    buildGateConfig,
    show,
    hide,
    release,
    HANDOVER_CAP_MS,
    CONSENT_WRITE_TIMEOUT_MS,
    checkAndShow,
    recordConsents,
    CURRENT_TOS_VERSION,
    CURRENT_PRIVACY_VERSION,
    CURRENT_MARKETING_VERSION,
    CURRENT_EMAIL_MARKETING_VERSION,
  };
})();

export { ConsentGate };
